import { type TourId } from '../onboardingStore';

export interface TourStep {
  id: string;
  /** CSS selector for the target element, null for centered (no spotlight) */
  targetSelector: string | null;
  placement: 'top' | 'bottom' | 'left' | 'right' | 'center';
  title: string;
  content: string;
  /** Extra padding around the spotlight cutout in px (default 8) */
  highlightPadding?: number;
}

export interface TourDefinition {
  id: TourId;
  steps: TourStep[];
}

export const TOUR_DEFINITIONS: Record<string, TourDefinition> = {
  main: {
    id: 'main',
    steps: [
      {
        id: 'nav-bar',
        targetSelector: 'nav[aria-label="Main navigation"]',
        placement: 'bottom',
        title: 'Navigation',
        content:
          'Each icon leads to a different tool \u2014 from the code editor to 3D geometry to simulation.',
      },
      {
        id: 'file-toolbar',
        targetSelector: '[data-onboarding="file-toolbar"]',
        placement: 'bottom',
        title: 'File Toolbar',
        content: 'Create, import, save, and export your EnergyPlus models here.',
      },
      {
        id: 'quick-action-new',
        targetSelector: '[data-onboarding="quick-action-new"]',
        placement: 'top',
        title: 'Get Started',
        content:
          'Start here \u2014 create a new blank model or import an existing IDF file.',
      },
      {
        id: 'ai-chat-button',
        targetSelector: '[data-onboarding="ai-chat-button"]',
        placement: 'left',
        title: 'AI Assistant',
        content:
          'Your AI assistant can read, modify, and validate your entire model through natural conversation.',
        highlightPadding: 12,
      },
      {
        id: 'simulate-link',
        targetSelector: 'a[href="/simulate"]',
        placement: 'bottom',
        title: 'Simulate',
        content:
          'Run EnergyPlus simulations directly in your browser \u2014 no server needed.',
      },
      {
        id: 'theme-toggle',
        targetSelector: '[data-onboarding="theme-toggle"]',
        placement: 'left',
        title: 'Theme',
        content: 'Switch between light and dark themes. Your preference is remembered.',
      },
      {
        id: 'tour-complete',
        targetSelector: null,
        placement: 'center',
        title: "You're all set!",
        content:
          'Create a new model or import an existing one to begin. You can replay this tour anytime from Settings.',
      },
    ],
  },
};

export function getTourSteps(tourId: TourId): TourStep[] {
  return TOUR_DEFINITIONS[tourId]?.steps ?? [];
}
